import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Box, ButtonProps, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import {
  ChatShareIcon,
  DislikeIcon,
  EditIcon,
  GradientLogo,
  LikeIcon,
  PdfSvg,
  SourceIcon,
} from "@root/assets/icons";
import { CustomText } from "@root/components/common/components";
import { getProjectsIconById } from "@root/utils/chatHelper";
import { DOCUMENT_REDIRECT_URL, getProjectByID } from "@root/webConstants";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import {
  StartCenterFlex,
  StyledAnimatedBtn,
  StyledAvatar,
  StyledPDFItem,
  StyledReactionItem,
  StyledWithoutPDFMoreSrcCard,
  StyledWithoutPDFSrcCard,
  StyledWithPDFSrcCard,
} from "../style";
import { LoaderLogo } from "@root/assets/icons";

interface IChatAvatar {
  role: ROLES;
  userName: string;
  uid: string;
  avatarSrc: string;
  chat?: any;
}

export const UserAvatar = (props: IChatAvatar) => {
  const { t } = useTranslation("common");
  const { role, userName, uid, avatarSrc, chat } = props;

  return (
    <Box>
      {role === "assistent" && (
        <Flex alignItems={"center"} gap={"16px"} mb={"12px"}>
          <Image
            width={"40px"}
            height={"40px"}
            src={getProjectsIconById(chat?.pid)}
            alt={`project_${chat?.pid}`}
          />
          <CustomText
            fontSize={"16px"}
            text={getProjectByID(chat?.pid)?.name}
            fontWeight={700}
          />
        </Flex>
      )}
      {role === "user" && (
        <>
          <Flex alignItems={"center"} gap={"16px"} mb={"12px"}>
            <StyledAvatar
              borderRadius={"10px"}
              name={uid ? t("translate.anonymous") : userName}
              size={"40px"}
              src={avatarSrc ? avatarSrc : ""}
            />
            <CustomText
              fontSize={"16px"}
              text={uid ? t("translate.anonymous") : t("translate.you")}
              fontWeight={700}
            />
          </Flex>
        </>
      )}
    </Box>
  );
};

export function PDFItem({ text }: any) {
  const { t } = useTranslation("common");
  return (
    <StyledPDFItem>
      <CustomText
        color={"theme.gray.gray500to300"}
        text={`${t("translate.@from")}`}
      />
      <PdfSvg width="17.5px" height="20px" />
      <CustomText
        color={"theme.gray.gray500to300"}
        text={`${text?.split("/")?.[1]}.pdf`}
      />
    </StyledPDFItem>
  );
}

export const SourceCardWithoutPDF = ({ src, index, maxWidth }: any) => {
  const onCardClick = () => {
    window.open(`${DOCUMENT_REDIRECT_URL}/${src?.filename}.pdf`, "_blank");
  };
  return (
    <StyledWithoutPDFSrcCard
      onClick={onCardClick}
      {...(maxWidth && { maxWidth })}
    >
      <Flex justifyContent={"space-between"}>
        <CustomText
          pr={"16px"}
          noOfLines={2}
          fontWeight={500}
          text={`${index + 1}. ${src?.heading}`}
        />
        <ChatShareIcon stroke={"theme.text.primaryDark"} />
      </Flex>
      <CustomText
        noOfLines={3}
        fontSize={"12px"}
        text={`${src?.page_content}`}
        color={"theme.gray.400"}
      />
    </StyledWithoutPDFSrcCard>
  );
};

export const SourceCardWithPDF = ({ src, index, maxWidth }: any) => {
  const onCardClick = () => {
    window.open(`${DOCUMENT_REDIRECT_URL}/${src?.filename}.pdf`, "_blank");
  };
  return (
    <StyledWithPDFSrcCard onClick={onCardClick} {...(maxWidth && { maxWidth })}>
      <Flex alignItems={"center"} gap={"8px"}>
        <PdfSvg width="35px" height="40px" />
        <CustomText
          pr={"16px"}
          noOfLines={2}
          fontWeight={500}
          text={`${index + 1}. ${src?.heading}`}
        />
      </Flex>
      <ChatShareIcon stroke={"theme.text.primaryDark"} />
    </StyledWithPDFSrcCard>
  );
};

export const MoreCardItemWithoutPDF = ({
  uniqueSources,
  lg,
  md,
  setSourcePopup,
}: any) => {
  const { t } = useTranslation("common");
  return (
    <StyledWithoutPDFMoreSrcCard onClick={() => setSourcePopup(true)}>
      <Flex justifyContent={"space-between"} flexDirection={"column"} flex={1}>
        <CustomText
          fontWeight={400}
          fontSize={"24px"}
          text={`${uniqueSources?.length - (lg ? 3 : md ? 2 : 1)}`}
        />
        <CustomText
          fontSize={"12px"}
          text={t("translate.moreSrcs")}
          color={"theme.gray.400"}
        />
      </Flex>
      <ChevronRightIcon
        cursor={"pointer"}
        margin={"auto"}
        width={"32px"}
        height={"32px"}
      />
    </StyledWithoutPDFMoreSrcCard>
  );
};

const ReactionItem = ({ onClick, image, text, fontSize, color }: any) => {
  return (
    <StyledReactionItem onClick={onClick}>
      {image && image}
      {text && (
        <CustomText
          fontWeight={700}
          text={text}
          fontSize={fontSize || "12px"}
          color={color || "currentcolor"}
        />
      )}
    </StyledReactionItem>
  );
};

export const UserReactions = (props: any) => {
  const { t } = useTranslation("common");
  const {
    sharePopupHandler,
    copyTextToClipboard,
    copyText,
    showToast,
    uid,
    onActivityClick,
    status,
    onCopy,
  } = props;
  return (
    <Flex
      marginTop={"-60px"}
      marginBottom={"-20px"}
      marginLeft={"auto"}
      marginRight={"16px"}
      justifyContent="center"
      alignItems="center"
      width="85px"
      height="45px"
      border={"2px solid"}
      borderColor={"theme.gray.25"}
      borderRadius="10px"
      cursor="pointer"
      bg={"border.inverseGray"}
      onClick={() => {
        onCopy();
        showToast();
      }}
    >
      <Flex gap={"32px"}>
        <ReactionItem text={t("translate.copy")} />
      </Flex>
    </Flex>
  );
};

export const UserSoures = (props: any) => {
  const { t } = useTranslation("common");
  const { uniqueSources, lg, md, setSourcePopup, isDocChat } = props;
  return (
    <Box>
      <ReactionItem
        color={"theme.text.primaryDark"}
        text={t("Sourcess")}
        image={<SourceIcon stroke={"theme.text.primaryDark"} />}
        fontSize={"16px"}
      />
      <Flex
        flexDir={{ base: "column", sm: "row" }}
        mt={"20px"}
        gap={"20px"}
        flexWrap={"wrap"}
      >
        {!isDocChat &&
          uniqueSources
            ?.slice(0, lg ? 3 : md ? 2 : 1)
            ?.map((src: any, i: number) => {
              return <SourceCardWithoutPDF key={i} index={i} src={src} />;
            })}
        {isDocChat &&
          uniqueSources
            ?.slice(0, lg ? 3 : md ? 2 : 1)
            ?.map((src: any, i: number) => {
              return <SourceCardWithPDF key={i} index={i} src={src} />;
            })}
        {/* In case of more Sourse */}
        {uniqueSources?.length - (lg ? 3 : md ? 2 : 1) > 0 && (
          <MoreCardItemWithoutPDF
            uniqueSources={uniqueSources}
            lg={lg}
            md={md}
            setSourcePopup={setSourcePopup}
          />
        )}
      </Flex>
    </Box>
  );
};

export const UserEditOptions = (props: any) => {
  const {
    childChats,
    chatIndex,
    onBack,
    childChatsLength,
    onNext,
    setEditable,
  } = props;
  // totalChats = editedChats + initial parent chat
  const totalChats = childChatsLength + 1;
  // currentChatIndex = edited chat chatIndex + initial parent chat index + plus 1 more because 0 is for devs not for user
  const currentChatIndex = chatIndex + 1 + 1;

  return (
    <Flex pt={"10px"} gap={"12px"} userSelect={"none"}>
      {childChats && childChats?.length > 0 && (
        <StartCenterFlex gap={"3px"}>
          <ChevronLeftIcon
            cursor={"pointer"}
            onClick={() => chatIndex + 1 > 0 && onBack()}
          />
          <CustomText text={`${currentChatIndex}/${totalChats}`} />
          <ChevronRightIcon
            onClick={() => chatIndex + 1 < childChatsLength && onNext()}
            cursor={"pointer"}
          />
        </StartCenterFlex>
      )}
      <EditIcon onClick={() => setEditable(true)} />
    </Flex>
  );
};

export const AnimatedButton = (props: any) => {
  return (
    <StyledAnimatedBtn {...props}>
      <Box display="flex" alignItems="center" gap="4px">
        <Text as="span" fontWeight="bold" fontStyle="italic" color="#005EB8">
          {t("translate.DOSAANAI")}
        </Text>
        <Text as="span" color="black">
          {t("translate.isThinking")}...
        </Text>
      </Box>
    </StyledAnimatedBtn>
  );
};

export { ReactionItem };
